import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { MessageCircle, Trash2, X } from 'lucide-react';
import { CHAT_SESSIONS_QUERY } from '../../graphql/queries';
import { DELETE_CHAT_SESSION_MUTATION } from '../../graphql/mutations';
import { FOCUS_RING_CLASSES, trapTabKey } from '../../lib/focusTrap';
import { useScrollLock } from '../../hooks/useScrollLock';
import { ConfirmDialog } from '../ConfirmDialog';

interface SessionSummary {
  id: string;
  title: string;
  state: string;
  updatedAt: string;
}

interface SessionListSheetProps {
  /** Open a conversation (the host navigates); the sheet closes itself first. */
  onOpenSession: (sessionId: string) => void;
  onClose: () => void;
  /** Highlighted as the current conversation. */
  activeSessionId?: string;
}

/** Compact relative time for the session list (locale-light, no dependency). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Chat history sheet: the caller's sessions, newest activity first — open one,
 * or delete one (with confirmation). Same bottom-sheet dialect as the other
 * overlays (scroll lock, focus trap, Escape).
 */
export function SessionListSheet({
  onOpenSession,
  onClose,
  activeSessionId,
}: SessionListSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<SessionSummary | null>(null);
  const [deleteError, setDeleteError] = useState(false);
  useScrollLock(true);

  const { data, loading, refetch } = useQuery<{ chatSessions: SessionSummary[] }>(
    CHAT_SESSIONS_QUERY,
    { fetchPolicy: 'cache-and-network' }
  );
  const [deleteSession] = useMutation(DELETE_CHAT_SESSION_MUTATION);

  const sessions = data?.chatSessions ?? [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  const runDelete = async (session: SessionSummary): Promise<void> => {
    setConfirmDelete(null);
    setDeleteError(false);
    try {
      await deleteSession({ variables: { id: session.id } });
      await refetch();
    } catch {
      setDeleteError(true);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 shadow-warm animate-sheet-up max-h-[min(85dvh,100%)] overflow-y-auto overscroll-contain safe-bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-list-title"
        tabIndex={-1}
      >
        <div
          aria-hidden
          className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border)] sm:hidden"
        />
        <div className="flex items-center justify-between mb-4">
          <h2 id="session-list-title" className="text-xl font-semibold text-[var(--text-primary)]">
            Conversations
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors ${FOCUS_RING_CLASSES}`}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
          </button>
        </div>

        {deleteError && (
          <div
            role="alert"
            className="mb-4 bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl"
          >
            Could not delete that conversation. Try again.
          </div>
        )}

        {loading && sessions.length === 0 ? (
          <div className="space-y-3" aria-hidden>
            <div className="skeleton h-12 rounded-xl" />
            <div className="skeleton h-12 rounded-xl" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)]">
            No conversations yet — tell me something and it will appear here.
          </p>
        ) : (
          <ul className="space-y-1 list-none">
            {sessions.map((session) => (
              <li key={session.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onOpenSession(session.id)}
                  aria-current={session.id === activeSessionId ? 'true' : undefined}
                  className={`flex-1 min-w-0 flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] ${FOCUS_RING_CLASSES} ${
                    session.id === activeSessionId
                      ? 'bg-[color-mix(in_oklab,var(--text-primary)_6%,transparent)]'
                      : ''
                  }`}
                >
                  <MessageCircle
                    className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]"
                    aria-hidden
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm font-medium text-[var(--text-primary)]">
                      {session.title}
                    </span>
                    <span className="block text-xs text-[var(--text-tertiary)]">
                      {relativeTime(session.updatedAt)}
                      {session.state === 'awaiting_approval' && ' · waiting for your approval'}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(session)}
                  className={`p-2 rounded-full text-[var(--text-tertiary)] hover:bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] hover:text-[var(--danger)] transition-colors ${FOCUS_RING_CLASSES}`}
                  aria-label={`Delete conversation "${session.title}"`}
                >
                  <Trash2 className="w-4 h-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete this conversation?"
        body={
          confirmDelete
            ? `"${confirmDelete.title}" will be deleted. Documents it saved stay in your wiki.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={() => confirmDelete && void runDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
